import React from 'react'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { themeConstants } from '@/styles/theme-constants.ts'
import {
  RankCardData,
  maskedTextStyle,
  inactiveIdentityStyle,
  pnlColor,
} from './ranking-card.types.ts'
import { RankTag } from './rank-tag.tsx'
import { ReturnPill } from '@/common/components/return-pill.tsx'

export interface RankCardColors {
  cardBg?: string
  cardOutline?: string
  // Border for the rank-4+ list cards (neutral, distinct from the gold podium).
  listCardOutline?: string
  // Border for the self row/card (primary brand border, distinct from others).
  selfCardOutline?: string
  linkColor?: string
  tertiaryTextColor?: string
  tagBg?: string
  longBg?: string
  longText?: string
  shortBg?: string
  shortText?: string
}

interface RankingCardProps {
  data: RankCardData
  // Resolved, formatted metric strings passed in so the card stays pure.
  returnPctText: string
  cumulativePnlText: string
  dailyPnlText: string
  dailyLabel: string
  colors: RankCardColors
  elevated?: boolean
  // Overrides the card background (self card uses an opaque surface).
  backgroundColor?: string
  onClick?: () => void
}

// List card for ranks 4+ (also reused by the floating self card). Pure
// presentational: every value and string arrives via props, so it renders
// deterministically from fixtures alone. Memoized so filtering/re-renders skip
// cards whose (stable) props are unchanged.
export const RankingCard = React.memo(function RankingCard({
  data,
  returnPctText,
  cumulativePnlText,
  dailyPnlText,
  dailyLabel,
  colors,
  elevated = false,
  backgroundColor,
  onClick,
}: RankingCardProps) {
  // Self rows/cards use the primary brand border; other ranks use the neutral
  // list border.
  const cardOutline = data.isSelf
    ? colors.selfCardOutline
    : (colors.listCardOutline ?? colors.cardOutline)
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: themeConstants.sizeSM,
        padding: `${themeConstants.sizeXS} ${themeConstants.sizeBase}`,
        borderRadius: themeConstants.borderRadiusLG,
        border: `1px solid ${cardOutline ?? 'transparent'}`,
        backgroundColor: backgroundColor ?? colors.cardBg,
        boxShadow: elevated ? '0 4px 16px rgba(0, 0, 0, 0.24)' : undefined,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <RankTag
          rank={data.rank}
          rankTotal={data.rankTotal}
          isSelf={data.isSelf}
        />
      </div>

      <div style={{ flex: '1 0 0', minWidth: 0 }}>
        <RankingCardIdentity
          data={data}
          linkColor={colors.linkColor}
          onClick={onClick}
        />
        {data.score && (
          <div>
            <IdeyaTypography.Text
              size={'sm'}
              style={{ color: colors.tertiaryTextColor }}
            >
              (★
              <span style={data.isMasked ? maskedTextStyle : undefined}>
                {data.score}
              </span>
              )
            </IdeyaTypography.Text>
          </div>
        )}
      </div>

      <ReturnPill
        value={data.returnPct}
        text={returnPctText}
        colors={colors}
        width={data.returnPct === 0 ? '54px' : '78px'}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: themeConstants.sizeXXS,
          flex: '0 0 102px',
          minWidth: 0,
        }}
      >
        <IdeyaTypography.Text
          textStyle={'strong'}
          style={{
            color: pnlColor(data.cumulativePnl, colors),
            fontVariantNumeric: 'tabular-nums',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {cumulativePnlText}
        </IdeyaTypography.Text>
        {/* Amount truncates with an ellipsis; the "Daily" label never shrinks. */}
        <div style={DAILY_ROW_STYLE}>
          <IdeyaTypography.Text
            size={'sm'}
            textStyle={'strong'}
            style={{ ...DAILY_AMOUNT_STYLE, color: colors.tertiaryTextColor }}
          >
            {dailyPnlText}
          </IdeyaTypography.Text>
          <IdeyaTypography.Text
            size={'sm'}
            textStyle={'strong'}
            style={{ ...DAILY_LABEL_STYLE, color: colors.tertiaryTextColor }}
          >
            {dailyLabel}
          </IdeyaTypography.Text>
        </div>
      </div>
    </div>
  )
})

interface RankingCardIdentityProps {
  data: RankCardData
  linkColor?: string
  onClick?: () => void
}

const DAILY_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: themeConstants.sizeXXS,
  maxWidth: '100%',
  minWidth: 0,
}

const DAILY_AMOUNT_STYLE: React.CSSProperties = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
}

const DAILY_LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  flexShrink: 0,
  whiteSpace: 'nowrap',
}

// Truncate a long identity to a single line with an ellipsis.
const ellipsisStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

// Identity element: masked rows show `*****`, openable rows render as a link,
// everything else is plain text.
const RankingCardIdentity: React.FC<RankingCardIdentityProps> = ({
  data,
  linkColor,
  onClick,
}) => {
  if (data.isMasked) {
    return (
      <IdeyaTypography.Text style={ellipsisStyle}>
        <span style={maskedTextStyle}>*****</span>
      </IdeyaTypography.Text>
    )
  }

  if (data.href) {
    return (
      <a
        href={data.href}
        target={'_blank'}
        rel={'noopener noreferrer'}
        title={data.identity}
        style={{
          color: linkColor,
          textDecoration: 'underline',
          cursor: 'pointer',
          ...ellipsisStyle,
          // Shrink the hit area to the text width, not the whole cell.
          display: 'inline-block',
          maxWidth: '100%',
        }}
        onClick={(e) => {
          // The card may itself be clickable; keep the link from double-firing.
          e.stopPropagation()
          onClick?.()
        }}
      >
        {data.identity}
      </a>
    )
  }

  return (
    <IdeyaTypography.Text
      style={
        data.isInactive
          ? { ...ellipsisStyle, ...inactiveIdentityStyle }
          : ellipsisStyle
      }
    >
      {data.identity}
    </IdeyaTypography.Text>
  )
}
